import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/_ui/Accordion'
import { Button } from '@/components/_ui/Button'
import { ErrorAlert } from '@/components/_ui/ErrorAlert'
import { Separator } from '@/components/_ui/Separator'
import { Skeleton } from '@/components/_ui/Skeleton'
import T from '@/components/Helpers/Translate'
import { useModals } from '@/components/ModalManager/useModals'
import { getSkuAvailability } from '@/lib/hooks/services/useGetSkuAvailability'
import type { HydraGetSkuAvailabilityResponse } from '@/lib/services/Hydra/order/getSkuAvailability'
import { TrackAddToCart } from '@/lib/services/mixpanel/events/cartEvents'
import { TrackSubscriptionSelected } from '@/lib/services/mixpanel/events/subscriptionEvents'
import { cn } from '@/lib/utils'
import type { CartItemType } from '@/store/types/cart'
import useGlobalStore from '@/store/useGlobalStore'
import { useState } from 'react'
import { SubscriptionAccordionLineItem } from '../../_helpers/components/SubscriptionAccordionLineItem'
import { getSubscriptionNextShippingDate } from '../../_helpers/getSubscriptionNextShippingDate'
import { useGetIfThereIsEnoughTimeToShip } from '../../_helpers/hooks/useGetIfThereIsEnoughTimeToShip'
import { SelectedActiveSubscriptionBackOrderAlert } from '../SelectedActiveSubscriptionBackOrderAlert'
import { TooCloseToShipDateAlert } from '../ToCloseToShipDateAlert'

type Props = {
  itemToAddToSubscription: CartItemType
  isLoadingProductAvailability: boolean
  productAvailability: HydraGetSkuAvailabilityResponse | null
}

export const SelectActiveSubscriptionList = ({
  itemToAddToSubscription,
  isLoadingProductAvailability,
  productAvailability,
}: Props) => {
  const globalStore = useGlobalStore()
  const { modals } = useModals()

  const {
    app: {
      addProductToSubscription: { selectedSubscriptionId },
      alpha2,
    },
    user: { customerType },
    app: { marketExceptions },
    subscriptions,
    setAppAddProductToSubscription,
    addToCart,
    setApp,
  } = globalStore

  const { isThereEnoughTimeToShip } = useGetIfThereIsEnoughTimeToShip()

  const [isAddingItemToCart, setIsAddingItemToCart] = useState(false)

  const skuAvailability = getSkuAvailability({
    sku: itemToAddToSubscription.product.sku,
    productAvailability,
  })

  if (subscriptions.isLoading || isLoadingProductAvailability) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-14 w-full" />
      </div>
    )
  }

  if (!subscriptions || !subscriptions.items) return null

  if (subscriptions && subscriptions.error) {
    return <ErrorAlert error={subscriptions.error} />
  }

  return (
    <div>
      <Accordion className="w-full" type="single" collapsible>
        {subscriptions.items.map((subscription, index) => {
          if (subscription.market !== alpha2) {
            return null
          }

          // If every item in the subscription doesn't have product data we skip it
          if (subscription.lines.items.every((line) => !line.productData)) {
            return null
          }

          return (
            <div key={subscription.href} className="mt-3 flex flex-col">
              <AccordionItem
                className="overflow-hidden"
                value={subscription.id.unicity}
              >
                <AccordionTrigger
                  className={cn(
                    'rounded-lg border border-solid border-[#99BCDF] py-3 pr-3 text-sm font-semibold hover:no-underline lg:py-3',
                    selectedSubscriptionId === subscription.id.unicity &&
                      'border-2',
                  )}
                  onClick={() => {
                    TrackSubscriptionSelected({
                      scenario: 'existing',
                      subscriptionId: subscription.id.unicity,
                    }).catch(() => {})

                    setIsAddingItemToCart(false)
                    setAppAddProductToSubscription({
                      selectedSubscriptionId: subscription.id.unicity,
                      selectedSubscriptionIndex: index + 1,
                    })
                  }}
                >
                  <div className="m-0 flex items-center space-x-2 pl-3">
                    <input
                      type="radio"
                      name="radio"
                      checked={
                        subscription.id.unicity === selectedSubscriptionId
                      }
                      onChange={() => {}} // Empty to avoid warning, this input is controlled
                    />

                    <div className="w-full flex-col items-center justify-between md:flex md:flex-row md:space-x-1">
                      <div className="flex space-x-1">
                        <T>subscription</T>
                        <span>{index + 1}</span>
                      </div>
                      <span className="hidden md:block">-</span>
                      <div className="flex space-x-1 font-light">
                        <span>
                          <T>next_ship_date</T>
                        </span>
                        <span>:</span>
                        <span>
                          {getSubscriptionNextShippingDate({ subscription })}
                        </span>
                      </div>
                      <span className="hidden font-light md:block">|</span>
                      <div className="flex font-light md:max-w-[25%]">
                        <span className="truncate">
                          {subscription.shipToAddress.address1}
                        </span>
                      </div>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="mx-2 rounded-b-lg bg-[#99bcdf26] p-3.5 px-5 md:p-5 md:px-10">
                    {subscription.lines.items.map((line) => {
                      if (!line.productData) return null

                      return (
                        <SubscriptionAccordionLineItem
                          key={`${line.item.id.unicity}-${line.item.id.unicity}`}
                          line={line}
                        />
                      )
                    })}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </div>
          )
        })}
      </Accordion>
      <div
        role="button"
        tabIndex={0}
        onClick={() => {
          setIsAddingItemToCart(true)

          setAppAddProductToSubscription({
            selectedSubscriptionId: null,
          })
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            setIsAddingItemToCart(true)

            setAppAddProductToSubscription({
              selectedSubscriptionId: null,
            })
          }
        }}
        className={cn(
          'mt-3 cursor-pointer rounded-lg border border-solid border-[#99BCDF] py-3 pr-3 text-sm font-semibold hover:no-underline lg:py-3',
          isAddingItemToCart && 'border-2',
        )}
      >
        <div className="m-0 flex items-center space-x-2 pl-3">
          <input checked={isAddingItemToCart} type="radio" name="radio" />

          <div className="ml-5 font-bold">
            <T>create_new_subscription</T>
          </div>
        </div>
      </div>
      <Separator className="my-5" />
      <div className="my-5">
        <TooCloseToShipDateAlert
          isThereEnoughTimeToShip={isThereEnoughTimeToShip}
        />
        <SelectedActiveSubscriptionBackOrderAlert
          isAddingItemToCart={isAddingItemToCart}
          skuAvailability={skuAvailability}
        />
        <div className="mt-3 flex justify-center">
          <Button
            disabled={!isAddingItemToCart && !selectedSubscriptionId}
            className="w-full max-w-sm"
            variant="secondary"
            onClick={() => {
              if (isAddingItemToCart) {
                TrackSubscriptionSelected({ scenario: 'new' }).catch(() => {})
                TrackAddToCart({
                  cartItem: itemToAddToSubscription,
                  eventLocation: 'product_page_main_button',
                  itemCount: itemToAddToSubscription.qty,
                  customerType,
                  currencyCode: marketExceptions?.currencyCode || 'USD',
                }).catch(() => {})
                setApp({ isCartOpen: true })
                addToCart({
                  ...itemToAddToSubscription,
                  addToCartLocation: ['product_page_main_button'],
                })

                // Close quick add modal if open.
                modals.closeModals()

                setAppAddProductToSubscription({
                  isSelectActiveSubscriptionModalOpen: false,
                })

                return
              }

              setAppAddProductToSubscription({
                isSelectActiveSubscriptionModalOpen: false,
                isConfirmAddToActiveSubscriptionModalOpen: true,
              })
            }}
          >
            <T>continue</T>
          </Button>
        </div>
      </div>
    </div>
  )
}
